import React, { useState, useEffect } from "react";

const CYCLING_MESSAGES = [
  "Applying theme...",
  "Preparing your template...",
  "Setting up sections...",
  "Almost ready...",
];

const RemixModalLoader = (props) => {
  const {
    statusMessage: externalStatusMessage = "Creating...",
    progress = null,
    showAiGeneratingStep = false,
    showSteps = true,
    bottomMessage = "This may take a moment.",
  } = props;

  const [cyclingIndex, setCyclingIndex] = useState(0);

  const hasAiProgress = progress != null && showAiGeneratingStep;
  const displayProgress = progress != null
    ? Math.min(100, Math.max(0, progress))
    : null;

  const cyclingMessages = externalStatusMessage
    ? [externalStatusMessage, ...CYCLING_MESSAGES]
    : CYCLING_MESSAGES;
  const resolvedStatusMessage =
    showAiGeneratingStep
      ? "AI generating content..."
      : cyclingMessages[cyclingIndex % cyclingMessages.length];

  useEffect(() => {
    if (showAiGeneratingStep) return;
    const interval = setInterval(() => {
      setCyclingIndex((i) => i + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, [showAiGeneratingStep]);

  const aiStepActive =
    showAiGeneratingStep && (progress == null || progress < 100);

  const steps = [
    { label: "Building template layout", complete: true, active: false },
    { label: "Applying styles", complete: true, active: false },
    ...(showAiGeneratingStep
      ? [
          {
            label: "AI generating content",
            complete: progress != null && progress >= 100,
            active: aiStepActive,
          },
        ]
      : []),
    { label: "Finalizing", complete: false, active: false },
  ];

  return (
    <div className="remix-modal-loader flex flex-1 flex-col items-center justify-center px-6 py-4 min-h-[400px] w-full">
      <div className="flex flex-col items-center gap-4 w-full max-w-md">
        <div className="w-16 h-16 animate-spin" style={{ flexShrink: 0 }}>
          <svg
            className="w-full h-full text-[#003FFF]"
            viewBox="0 0 36 36"
            fill="none"
          >
            <circle
              cx="18"
              cy="18"
              r="16"
              stroke="currentColor"
              strokeWidth="3"
              strokeOpacity="0.2"
            />
            <path
              d="M18 2a16 16 0 0 1 16 16"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="text-center">
          <h3 className="text-[20px] mb-[15px] font-semibold text-slate-800 dark:text-slate-200 m-0 transition-opacity duration-300">
            {resolvedStatusMessage}
          </h3>
          <p className="text-xl text-slate-500 mt-1">
            {displayProgress != null
              ? `${Math.round(displayProgress)}% complete`
              : "Working..."}
          </p>
        </div>

        <div className="w-full">
          <div
            className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
            style={{ width: "100%" }}
          >
            {displayProgress != null ? (
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${displayProgress}%`,
                  background: showAiGeneratingStep
                    ? "linear-gradient(90deg, #6B8CFF 0%, #003FFF 100%)"
                    : "linear-gradient(90deg, #003FFF 0%, #2563eb 100%)",
                }}
              />
            ) : (
              <div
                className="h-full rounded-full remix-loader-indeterminate"
                style={{
                  background: showAiGeneratingStep
                    ? "linear-gradient(90deg, transparent 0%, #6B8CFF 30%, #003FFF 70%, transparent 100%)"
                    : "linear-gradient(90deg, transparent 0%, #003FFF 30%, #2563eb 70%, transparent 100%)",
                  backgroundSize: "200% 100%",
                  animation: "remix-loader-slide 1.5s ease-in-out infinite",
                }}
              />
            )}
          </div>
        </div>

        {showSteps && (
        <div className="w-full flex flex-col gap-3">
          {steps.map((step, index) => {
            const isActive = step.active === true;
            const iconClass = step.complete
              ? "bg-blue-600 text-white"
              : isActive
                ? "border-2 border-blue-600 bg-blue-50 dark:bg-blue-950/30"
                : "border-2 border-slate-300 dark:border-slate-600";
            const textClass = step.complete
              ? "text-slate-700 dark:text-slate-300"
              : isActive
                ? "text-slate-800 dark:text-slate-200 font-medium"
                : "text-slate-400 dark:text-slate-500";

            return (
              <div
                key={index}
                className="flex items-center gap-3 text-xl"
              >
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${iconClass}`}
                >
                  {step.complete ? (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : isActive ? (
                    <div className="w-3 h-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  ) : null}
                </div>
                <span className={textClass}>{step.label}</span>
              </div>
            );
          })}
        </div>
        )}

        {bottomMessage && (
          <p className="text-sm text-slate-400 dark:text-slate-500 text-center mt-2">
            {bottomMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default RemixModalLoader;
